import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { type ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown, Loader } from 'lucide-react'
import { EnvelopeIcon } from '../Icons'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { paymentStatusClasses } from '@/utils/helper'

// Attendee data types based on Figma design
export interface Attendee {
  id: string
  name: string
  eventName: string
  email: string
  ticketType: string
  status: string
  purchaseDate: string
  totalPaid: string
  baseAmount: string
  feeAmount: string
  pendingAddonBookings: number
}

export type DisplayAttendee = Attendee

export const getAttendeeColumns = (
  onAttendeeClick?: (attendeeId: string) => void,
  onResendTicketClick?: (transactionId: string) => void,
  resendingId?: string | null
): ColumnDef<DisplayAttendee>[] => [
  {
    accessorKey: 'name',
    header: () => {
      return (
        <Button
          variant="ghost"
          className="h-auto font-medium text-ss-neutral-950 text-xs hover:bg-transparent justify-start w-32"
          aria-label="Sort by name"
        >
          Name
        </Button>
      )
    },
    cell: ({ row }) => {
      const attendee = row.original
      return (
        <div className="flex items-center gap-2.5">
          <button
            className="font-semibold text-sm text-ss-neutral-950 leading-4.5  w-32 wrap-anywhere text-left hover:underline transition-colors cursor-pointer hover:text-ss-blue-600"
            onClick={() => onAttendeeClick?.(attendee.id)}
            aria-label={`View details for ${attendee.name}`}
          >
            {attendee.name}
          </button>
        </div>
      )
    },
  },
  {
    accessorKey: 'eventName',
    header: () => {
      return (
        <Button
          variant="ghost"
          className="h-auto font-medium text-ss-neutral-950 text-xs hover:bg-transparent justify-start w-32"
          aria-label="Sort by event name"
        >
          Event Name
        </Button>
      )
    },
    cell: ({ row }) => {
      const attendee = row.original
      return (
        <span className="font-semibold text-sm text-ss-neutral-950 leading-4.5  wrap-anywhere w-32">
          {attendee.eventName}
        </span>
      )
    },
  },
  {
    accessorKey: 'email',
    header: () => {
      return (
        <Button
          variant="ghost"
          className="font-medium text-ss-neutral-950 text-xs hover:bg-transparent justify-start w-32"
          aria-label="Sort by email"
        >
          Email
        </Button>
      )
    },
    cell: ({ row }) => {
      const attendee = row.original
      return (
        <span className="font-semibold text-sm text-ss-neutral-600 leading-4.5  w-32 wrap-anywhere">
          {attendee.email}
        </span>
      )
    },
  },
  {
    accessorKey: 'status',
    header: () => {
      return (
        <Button
          variant="ghost"
          className="font-medium text-ss-neutral-950 text-xs hover:bg-transparent justify-start w-24"
          aria-label="Sort by status"
        >
          Status
        </Button>
      )
    },
    cell: ({ row }) => {
      const status = row.getValue('status') as string
      return (
        <div className="w-24  wrap-anywhere">
          <Badge
            className={cn(
              'px-2 py-1 text-xs font-medium rounded-full',
              paymentStatusClasses(status)
            )}
            aria-label={`Status: ${status}`}
          >
            {status}
          </Badge>
        </div>
      )
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="font-medium text-ss-neutral-950 text-xs hover:bg-transparent w-36 justify-start"
          aria-label="Sort by purchase date"
        >
          Purchase Date
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const attendee = row.original
      return (
        <span className="font-medium text-sm text-ss-neutral-600 leading-4.5  w-36  wrap-anywhere">
          {attendee.purchaseDate}
        </span>
      )
    },
  },
  {
    accessorKey: 'totalAmount',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className=" font-medium text-ss-neutral-950 text-xs hover:bg-transparent w-32 text-left justify-start"
          aria-label="Sort by total paid"
        >
          Total Paid
          <ArrowUpDown className="ml-1 h-3 w-3" />
        </Button>
      )
    },
    cell: ({ row }) => {
      const attendee = row.original
      return (
        <div className="text-left w-32">
          <p className="font-semibold text-sm text-ss-neutral-950 leading-4.5   wrap-anywhere">
            $ {attendee.baseAmount}
          </p>
        </div>
      )
    },
  },
  {
    id: 'actions',
    header: () => <div className="sr-only">Actions</div>,
    cell: ({ row }) => {
      const attendee = row.original
      return (
        <div className="flex items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Assign role to selected users"
                onClick={() => {
                  onResendTicketClick?.(attendee.id)
                }}
              >
                {resendingId === attendee.id ? (
                  <Loader className="size-5 animate-spin" aria-hidden="true" />
                ) : (
                  <EnvelopeIcon className="size-5" aria-hidden="true" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Re-Send Ticket</TooltipContent>
          </Tooltip>
        </div>
      )
    },
  },
]
